import { ApolloCache, useApolloClient } from '@apollo/client'
import { useCallback, useMemo } from 'react'

import type { CanvasActions } from '@factorialco/f0-react'
import { handleResult } from 'modules/core/lib/handleMutation'

import {
  CreateOneAnalyticsDashboardDocument,
  CreateOneAnalyticsDashboardMutation,
  FetchSavedDashboardByIdDocument,
  FetchSavedDashboardByIdQuery,
  FetchSavedDashboardDocument,
  OneAnalyticsDashboardManagementPolicyDocument,
  OneAnalyticsDashboardCategoryEnum,
  UpdateOneAnalyticsDashboardDocument,
  UpdateOneAnalyticsDashboardMutation,
} from 'generated/resources/graphql'

import { useReportDashboardsFeature } from 'modules/ai/hooks/useReportDashboardsFeature'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import buildUrl from 'modules/core/lib/buildUrl'

import { trackOneAnalyticsExplorationSaved } from './telemetry'

const VALID_CATEGORIES: OneAnalyticsDashboardCategoryEnum[] = [
  'analytics',
  'communities',
  'recruitment',
  'trainings',
]

const toCategory = (category?: string): OneAnalyticsDashboardCategoryEnum => {
  const found = VALID_CATEGORIES.find((c) => c === category)
  return found ?? 'analytics'
}

type DashboardSpecsConfig = {
  title?: string
  items: unknown
  fetchSpecs: unknown
  filters?: unknown
  navigationFilters?: unknown
  snapshot?: boolean
  snapshotDate?: string
  dateNavigatorColumn?: string
}

const configToSpecs = (config: DashboardSpecsConfig): string =>
  JSON.stringify({
    items: config.items,
    fetchSpecs: config.fetchSpecs,
    filters: config.filters ?? null,
    navigationFilters: config.navigationFilters ?? null,
    ...(config.snapshot === true ? { snapshot: true } : {}),
    ...(config.snapshotDate ? { snapshotDate: config.snapshotDate } : {}),
    ...(config.dateNavigatorColumn ? { dateNavigatorColumn: config.dateNavigatorColumn } : {}),
  })

export const useCanvasActions = (): CanvasActions | undefined => {
  const { company, employee } = useCurrentSession()
  const { enabled: reportDashboardsEnabled } = useReportDashboardsFeature()
  const { data: managementPolicyData } = useQuery(OneAnalyticsDashboardManagementPolicyDocument, {
    skip: !reportDashboardsEnabled || !employee,
  })
  const canManageDashboards = Boolean(
    managementPolicyData?.oneAnalytics?._policies?.manageDashboards?.can
  )
  const apolloClient = useApolloClient()

  // Invalidate the AI Reports listing's `dashboardsConnection` cache entries
  // after any create/update so that when the user navigates to the listing
  // (or it's already mounted elsewhere) it refetches and shows the new or
  // updated dashboard. Without this the listing renders from a stale cache
  // and the dashboard the user just saved appears missing.
  const evictDashboardsConnection = (cache: ApolloCache<unknown>) => {
    cache.evict({ id: 'oneAnalytics', fieldName: 'dashboardsConnection' })
    cache.evict({ fieldName: 'oneAnalytics' })
    cache.gc()
  }

  const [updateDashboard] = useMutation(UpdateOneAnalyticsDashboardDocument, {
    extractorFn: (data: UpdateOneAnalyticsDashboardMutation) =>
      data.oneAnalyticsMutations.updateOneanalyticsDashboard,
    refetchQueries: [FetchSavedDashboardDocument],
    update: (cache) => evictDashboardsConnection(cache),
  })

  const [createDashboard] = useMutation(CreateOneAnalyticsDashboardDocument, {
    extractorFn: (data: CreateOneAnalyticsDashboardMutation) =>
      data.oneAnalyticsMutations.createOneanalyticsDashboard,
    refetchQueries: [FetchSavedDashboardDocument],
    update: (cache) => evictDashboardsConnection(cache),
  })

  const save = useCallback(
    async (
      id: string,
      _category: string,
      config: {
        title: string
        items: unknown
        fetchSpecs: unknown
        filters?: unknown
        navigationFilters?: unknown
      }
    ) => {
      if (!employee) return

      try {
        const response = await updateDashboard({
          variables: {
            id,
            companyId: company.id,
            title: config.title,
            specs: configToSpecs(config),
          },
        })

        const handled = handleResult<{ dashboard?: { id: string } | null }>(
          response,
          () => undefined
        )
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
          trackOneAnalyticsExplorationSaved({
            config,
            dashboardId: handled.data.dashboard?.id ?? id,
            category: toCategory(_category),
            saveKind: 'update',
          })
        }
      } catch {
        app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
      }
    },
    [updateDashboard, company.id, employee]
  )

  const create = useCallback(
    async (
      title: string,
      description: string,
      config: {
        items: unknown
        fetchSpecs: unknown
        filters?: unknown
        navigationFilters?: unknown
      },
      category?: string
    ) => {
      try {
        const resolvedCategory = toCategory(category)
        const response = await createDashboard({
          variables: {
            title,
            description: description || undefined,
            category: resolvedCategory,
            specs: configToSpecs(config),
            companyId: company.id,
          },
        })

        const handled = handleResult<{ dashboard?: { id: string } | null }>(
          response,
          () => undefined
        )
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
          const id = handled.data.dashboard?.id
          if (!id) return undefined

          trackOneAnalyticsExplorationSaved({
            config,
            dashboardId: id,
            category: resolvedCategory,
            saveKind: 'create',
          })
          // f0's canvas transitions to the "saved" state only when both id
          // AND category are returned — `save` needs the category for every
          // subsequent update, so returning only the id would strand the UI.
          return { id, category: resolvedCategory }
        }
      } catch {
        app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
      }
    },
    [createDashboard, company.id]
  )

  const getMetadata = useCallback(
    async (id: string) => {
      // Network-only so reopening a saved dashboard after an external edit
      // (e.g. someone else updated the title from the Analytics list page)
      // reflects the latest author/updatedAt instead of a stale cached copy.
      // The header calls this once per id transition, so cost is bounded.
      try {
        const result = await apolloClient.query<FetchSavedDashboardByIdQuery>({
          query: FetchSavedDashboardByIdDocument,
          variables: { dashboardId: id },
          fetchPolicy: 'network-only',
        })

        const dashboard = result.data?.oneAnalytics?.dashboard
        if (!dashboard) return undefined

        return {
          // Prefer the persisted title/description so the header stays in
          // sync with any edit made outside the chat (list page, API, etc.)
          // instead of the potentially stale copy baked into chat history.
          title: dashboard.title,
          description: dashboard.description ?? undefined,
          creator: {
            firstName: dashboard.author.firstName,
            lastName: dashboard.author.lastName,
            src: buildUrl(dashboard.author.avatar?.url),
          },
          lastEdited: dashboard.updatedAt,
        }
      } catch {
        // Swallow — the header renders gracefully without metadata. Surfacing
        // an error flash here would be noisy for a read-only header decoration.
        return undefined
      }
    },
    [apolloClient]
  )

  const canvasActions = useMemo<CanvasActions>(
    () => ({
      dashboard: { save, create, getMetadata },
    }),
    [save, create, getMetadata]
  )

  if (!reportDashboardsEnabled || !canManageDashboards || !employee) return undefined

  return canvasActions
}
